<template>
  <v-dialog
    v-model="dialog"
    width="500"
    persistent
    scrollable
  >
    <template #activator="{ on, attrs }">
      <span v-if="listButton">
        <v-btn
          v-bind="attrs"
          v-on="on"
          class="d-none d-sm-block"
          text
          @click="init"
        >
          {{ $tc('supportTicketCreate.createButtonLabel', 1) }}
          <v-icon right>mdi-plus</v-icon>
        </v-btn>
      </span>
      <span v-else>
        <v-btn
          small
          plain
          v-bind="attrs"
          v-on="on"
          @click="init"
        >
          <v-icon left>mdi-plus</v-icon>
          <span v-if="$vuetify.breakpoint.mobile">
            {{ $t('common.new') }}
          </span>
          <span v-else>
            {{ $tc('supportTicketCreate.createButtonLabel', 1) }}
          </span>
        </v-btn>
      </span>
    </template>

    <v-card>
      <v-card-title class="title black--text accent">
        {{ !!createResult ? $t('supportTicketCreate.titleSuccess') : $t('supportTicketCreate.title') }}
      </v-card-title>

      <Loading :loading="!loaded" />

      <v-card-text v-if="loaded && model">
        <v-container
          v-if="!createResult"
          fluid
        >
          <v-row>
            <v-col>
              <v-text-field
                v-model="model.data.externalCode"
                :label="$t('supportTicketCreate.externalReference')"
              />

              <div class="mb-1 font-italic">
                {{ $t('supportTicketCreate.allRequired') }}
              </div>

              <v-autocomplete
                v-model="model.data.calledBy"
                :items="model.metadata.calledBys"
                item-text="text"
                item-value="value"
                :label="$tc('common.calledBy')"
                return-object
                :disabled="model.metadata?.calledBys?.length === 1"
                :search-input.sync="calledByInput"
                auto-select-first
                small-chips
                outlined
dense @input="calledByInput = null"
              />
              <v-autocomplete
                v-model="model.data.department"
                :items="model.metadata.departments"
                item-text="text"
                item-value="value"
                :label="$tc('common.department')"
                return-object
                :disabled="model.metadata?.departments?.length === 1"
                :search-input.sync="departmentInput"
                auto-select-first
                small-chips
                outlined
dense @input="departmentInput = null"
              />
              <v-autocomplete
                v-model="model.data.location"
                :items="model.metadata.locations"
                item-text="text"
                item-value="value"
                :label="$tc('common.location')"
                return-object
                :disabled="model.metadata?.locations?.length === 1"
                :search-input.sync="locationInput"
                auto-select-first
                small-chips
                outlined
dense @input="locationInput = null"
              />
              <v-autocomplete
                v-model="model.data.category"
                :items="model.metadata.categories"
                item-text="text"
                item-value="value"
                :label="$tc('common.category')"
                return-object
                :disabled="model.metadata?.categories?.length === 1"
                :search-input.sync="categoryInput"
                auto-select-first
                small-chips
                outlined
dense @input="categoryInput = null"
              />
              <v-autocomplete
                v-model="model.data.subjectAreas"
                :items="model.metadata.subjectAreas"
                item-text="text"
                item-value="value"
                :label="$tc('common.subjectArea', 2)"
                return-object
                :disabled="model.metadata?.subjectAreas?.length === 1"
                :search-input.sync="subjectInput"
                auto-select-first
                small-chips
                outlined
dense multiple @input="subjectInput = null"
              />
              
              <v-text-field
                v-model="model.data.title"
                outlined
                :label="$t('common.title')"
              />
              <v-textarea
                v-model="model.data.text"
                outlined
                auto-grow
                rows="3"
                :label="$t('common.description')"
              />
              <v-textarea
                v-model="model.data.contactDetails"
                outlined
                auto-grow
                rows="3"
                :label="$t('supportTicketCreate.contactDetailsLabel')"
              />
            </v-col>
          </v-row>
        </v-container>
        <div
          v-if="!!createResult"
          class="pt-4"
        >
          <div>
            {{ $t('supportTicketCreate.createdSuccessText') }}
          </div>
          <div>
            <router-link :to="`/supportticket/${createResult.model.id}`">
              {{ createResult.model.code }}
            </router-link>
          </div>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn
          v-if="!createResult"
          outlined
          color="primary"
          @click="cancel"
        >
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn
          v-if="!createResult"
          color="primary"
          :loading="saving"
          :disabled="!isValid"
          @click="create"
        >
          {{ $t('common.create') }}
        </v-btn>
        <v-btn
          v-if="!!createResult"
          outlined
          color="primary"
          @click="close"
        >
          {{ $t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import Loading from '@/components/Loading'

  export default {
    name: 'SupportTicketCreate',
    components: {
      Loading
    },
    props: {
      listButton: {
        default: true,
        type: Boolean,
      },
    },
    data () {
      return {
        model: Object,
        createResult: null,
        //view only
        dialog: false,
        loaded: false,
        saving: false,
        isValid: false,
        calledByInput: null,
        departmentInput: null,
        locationInput: null,
        categoryInput: null,
        subjectInput: null
      }
    },
    watch: {
      model: {
        deep: true,
        handler() {
          if (this.model.data) {
            const { calledBy, department, location, category, subjectAreas, title, contactDetails } = this.model.data;
  
            this.isValid = calledBy && department && location && category && subjectAreas && title && contactDetails;
          }
        }
      }
    },
    methods: {
      init: async function() {
        this.loaded = false;

        try {
          const { data } = await this.$api.get('supportticket/create');

          this.model = data;

          if (data.metadata?.calledBys?.length === 1) {
            this.model.data.calledBy = data.metadata.calledBys[0]
          }
          if (data.metadata?.departments?.length === 1) {
            this.model.data.department = data.metadata.departments[0]
          }
          if (data.metadata?.locations?.length === 1) {
            this.model.data.location = data.metadata.locations[0]
          }
          if (data.metadata?.categories?.length === 1) {
            this.model.data.category = data.metadata.categories[0]
          }
          if (data.metadata?.subjectAreas?.length === 1) {
            this.model.data.subjectAreas = data.metadata.subjectAreas
          }
        } catch (error) {
          this.$notifier.showError({});
        } finally {
          this.createResult = null;
          this.loaded = true;
        }
      },
      create() {
        this.saving = true;

        this.$api.post(`/SupportTicket/Create`, this.model.data).then(response => {
          if (response.data.success) {
            this.createResult = response.data;
            this.saving = false;
            this.$emit('saved');
          }
        });
      },
      cancel() {
        this.dialog = false;
      },
      close() {
        this.dialog = false;
      }
    }
  }
</script>